import React, { useState } from "react";

// Lib
import { Big } from "big.js";
import { packedValue } from "lib/calculator";

// Components
import Button from "components/UiKit/Button";
import FieldText from "components/UiKit/FieldText";
import FieldSelect from "components/UiKit/FieldSelect";

// Constants
import { selectStyle } from "constans/react-select-style";
import {
  optionsConcentrationEnd,
  optionsConcentrationStart,
  optionsVolumeEnd,
  optionsVolumeStart
} from "constans/molecule-product";

// Type
type TypeObject = {
  exp?: number;
  value?: number;
};
type TypeSelect = {
  label?: string,
  value?: number,
}
type TypeArgs = {
  volumeEnd: TypeObject;
  volumeStart: TypeObject;
  concentrationStart: TypeObject;
  concentrationEnd: TypeObject;
};

export const DilutionCalculator: React.FC = () => {

  const [volumeEnd, setVolumeEbd] = useState<string>("");
  const [volumeStart, setVolumeStart] = useState<string>("");

  const [concentrationEnd, setConcentrationEnd] = useState<string>("");
  const [concentrationStart, setConcentrationStart] = useState<string>("");

  const [volumeUnitEnd, setVolumeUnitEnd] = useState<number>(optionsVolumeEnd[1].value);
  const [volumeUnitStart, setVolumeUnitStart] = useState<number>(optionsVolumeStart[1].value);

  const [concentrationUnitEnd, setConcentrationUnitEnd] = useState<number>(optionsConcentrationEnd[1].value);
  const [concentrationUnitStart, setConcentrationUnitStart] = useState<number>(optionsConcentrationStart[1].value);

  const [errorMessage, setErrorMessage] = useState<string>("");

  const headlerReset = () => {
    setVolumeEbd("");
    setVolumeStart("");
    setConcentrationEnd("");
    setConcentrationStart("");

    setVolumeUnitEnd(optionsVolumeEnd[1].value);
    setVolumeUnitStart(optionsVolumeStart[1].value);
    setConcentrationUnitEnd(optionsConcentrationStart[1].value);
    setConcentrationUnitStart(optionsConcentrationStart[1].value);
  };

  const calculateDilution = (opt: TypeArgs) => {
    const {
      concentrationStart,
      volumeStart,
      concentrationEnd,
      volumeEnd
    } = opt;
    let emptyNumber = 0;
    let invalidNumber = 0;

    if (concentrationStart.value && (isNaN(concentrationStart.value) || +concentrationStart.value <= 0)) {
      invalidNumber += 1;
    } else if (!concentrationStart.value) {
      emptyNumber += 1;
    }

    if (volumeStart.value && (isNaN(volumeStart.value) || +volumeStart.value <= 0)) {
      invalidNumber += 1;
    } else if (!volumeStart.value) {
      emptyNumber += 1;
    }

    if (concentrationEnd.value && (isNaN(concentrationEnd.value) || +concentrationEnd.value <= 0)) {
      invalidNumber += 1;
    } else if (!concentrationEnd.value) {
      emptyNumber += 1;
    }

    if (volumeEnd.value && (isNaN(volumeEnd.value) || +volumeEnd.value <= 0)) {
      invalidNumber += 1;
    } else if (!volumeEnd.value) {
      emptyNumber += 1;
    }

    if (emptyNumber > 1) {
      throw new Error("Please fill in three blanks.");
    }

    if (emptyNumber === 0 && invalidNumber >= 1) {
      throw new Error("Please leave one item blank.");
    }

    if (invalidNumber) {
      throw new Error("Please enter numbers only.");
    }

    let bigResult: Big | number = 0;

    if (!concentrationStart.value && volumeStart.value && concentrationEnd.value && volumeEnd.value) {
      bigResult = new Big(concentrationEnd.value)
        .times(Math.pow(10, concentrationEnd.exp || 0))
        .times(volumeEnd.value)
        .times(Math.pow(10, volumeEnd.exp || 0))
        .div(volumeStart.value)
        .div(Math.pow(10, volumeStart.exp || 0));

      const resultPackedValue = packedValue({ bigValue: bigResult, maxExp: 12, minExp: 0 });
      setConcentrationStart(resultPackedValue.value);
      setConcentrationUnitStart(resultPackedValue.exp);
    } else if (concentrationStart.value && !volumeStart.value && concentrationEnd.value && volumeEnd.value) {
      bigResult = new Big(concentrationEnd.value)
        .times(Math.pow(10, concentrationEnd.exp || 0))
        .times(volumeEnd.value)
        .times(Math.pow(10, volumeEnd.exp || 0))
        .div(concentrationStart.value)
        .div(Math.pow(10, concentrationStart.exp || 0));

      const resultPackedValue = packedValue({ bigValue: bigResult, maxExp: 6, minExp: 0 });
      setVolumeStart(resultPackedValue.value);
      setVolumeUnitStart(resultPackedValue.exp);
    } else if (concentrationStart.value && volumeStart.value && !concentrationEnd.value && volumeEnd.value) {
      bigResult = new Big(concentrationStart.value)
        .times(Math.pow(10, concentrationStart.exp || 0))
        .times(volumeStart.value)
        .times(Math.pow(10, volumeStart.exp || 0))
        .div(volumeEnd.value)
        .div(Math.pow(10, volumeEnd.exp || 0));

      const resultPackedValue = packedValue({ bigValue: bigResult, maxExp: 12, minExp: 0 });
      setConcentrationEnd(resultPackedValue.value);
      setConcentrationUnitEnd(resultPackedValue.exp);
    } else if (concentrationStart.value && volumeStart.value && concentrationEnd.value && !volumeEnd.value) {
      bigResult = new Big(concentrationStart.value)
        .times(Math.pow(10, concentrationStart.exp || 0))
        .times(volumeStart.value)
        .times(Math.pow(10, volumeStart.exp || 0))
        .div(concentrationEnd.value)
        .div(Math.pow(10, concentrationEnd.exp || 0));

      const resultPackedValue = packedValue({ bigValue: bigResult, maxExp: 6, minExp: 0 });
      setVolumeEbd(resultPackedValue.value);
      setVolumeUnitEnd(resultPackedValue.exp);
    } else {
      throw new Error("Please leave one item blank.");
    }
  };

  const handleCalculate = () => {
    try {
      calculateDilution({
        volumeEnd: {
          value: +volumeEnd,
          exp: volumeUnitEnd
        },
        volumeStart: {
          value: +volumeStart,
          exp: volumeUnitStart
        },
        concentrationEnd: {
          value: +concentrationEnd,
          exp: concentrationUnitEnd
        },
        concentrationStart: {
          value: +concentrationStart,
          exp: concentrationUnitStart
        }
      });
    } catch (e: any) {
      setErrorMessage(e.message);
      return;
    }

    setErrorMessage("");
  };

  const styleSelectCalculator = {
    ...selectStyle,
    singleValue: (p: any) => ({
      ...p,
      fontWeight: "500",
      fontSize: "1.2rem",
      fontStyle: "normal",
      borderStyle: "unset",
      lineHeight: "1.2rem",
      letterSpacing: "0.8px",
      fontFamily: "Poppins"
    }),
    container: (provided: any) => ({
      ...provided,
      "&:hover, &:focus": {},
      height: "4rem",
      width: "7.2rem",
      color: "#060606",
      maxWidth: "100%",
      fontWeight: "500",
      fontSize: "1.4rem",
      fontStyle: "normal",
      lineHeight: "2.4rem",
      letterSpacing: "0.8px",
      fontFamily: "Poppins",

      "@media only screen and (max-width: 1024px)": {
        ...provided["@media only screen and (max-width: 1024px)"],
        maxWidth: "100%"
      }
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: "#9B9B9B",
      fontWeight: "500",
      fontSize: "1.4rem",
      fontStyle: "normal",
      lineHeight: "2.4rem",
      letterSpacing: "0.8px",
      fontFamily: "Poppins"
    }),
    menu: (provided: any) => ({
      ...provided,
      zIndex: 999,
      "&:hover, &:focus": {}
    }),
    control: (provided: any) => ({
      ...provided,
      "&:hover, &:focus": {
        borderColor: "unset"
      },
      height: "4rem",
      border: "unset",
      width: "7.2rem",
      color: "#060606",
      boxShadow: "none",
      maxWidth: "100%",
      cursor: "pointer",
      fontWeight: "500",
      fontSize: "1.3rem",
      fontStyle: "normal",
      borderStyle: "unset",
      lineHeight: "1.2rem",
      borderRadius: "unset",
      letterSpacing: "0.8px",
      fontFamily: "Poppins",
      backgroundColor: "transparent",
      borderBottom: "1px solid rgba(6, 6, 6, 0.25)",

      "@media only screen and (max-width: 1024px)": {
        ...provided["@media only screen and (max-width: 1024px)"],
        maxWidth: "100%"
      }
    })
  };

  return (
    <div className="dilution-calculator">
      <div className="dilution-calculator__bar flex flex-ai-center flex-jc-between mb30 full-width">
        <div>
          <h4 className="dilution-calculator__title mt20 mb20">The dilution calculator equation</h4>
          <div className="dilution-calculator__sub-title">
            <p>Concentration <sub>(start)</sub> × Volume <sub>(start)</sub> = Concentration <sub>(final)</sub> ×
              Volume <sub>(final)</sub></p>
            <p>This equation is commonly abbreviated
              as: <span>C<sub>1</sub>V<sub>1</sub> = C<sub>2</sub>V<sub>2</sub></span></p>
          </div>
        </div>

        <div className="flex flex-direction-column">
          <Button
            color="green"
            variable="primary"
            label="Calculate"
            onPress={handleCalculate}
            className="dilution-calculator__submit mb10"
          />
          <Button
            variable="outline"
            label="Reset"
            onPress={headlerReset}
            className="dilution-calculator__submit-clear"
          />
        </div>
      </div>
      <div>
        <div className="flex flex-ai-end flex-jc-start full-width mb30">
          <div className="flex flex-ai-end">
            <FieldText
              placeholder="Your value"
              name="concentrationStart"
              value={concentrationStart}
              label={`Concentration <sub>(start)</sub>`}
              className="dilution-calculator__input mr10"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConcentrationStart(e.target.value)}
            />
            <FieldSelect
              placeholder="Select"
              name="concentrationUnitStart"
              styles={styleSelectCalculator}
              options={optionsConcentrationStart}
              onChange={(opt: TypeSelect) => setConcentrationUnitStart(+(opt.value || 0))}
              value={optionsConcentrationStart.find((item) => item.value === concentrationUnitStart)}
            />
          </div>

          <span className="molarity-calculator__sign mb10 mr5 ml5">×</span>

          <div className="flex flex-ai-end">
            <FieldText
              name="volumeStart"
              value={volumeStart}
              placeholder="Your value"
              label={`Volume <sub>(start)</sub>`}
              className="dilution-calculator__input mr10"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setVolumeStart(e.target.value)}
            />
            <FieldSelect
              placeholder="Select"
              name="volumeUnitStart"
              options={optionsVolumeStart}
              styles={styleSelectCalculator}
              onChange={(opt: TypeSelect) => setVolumeUnitStart(+(opt?.value || 0))}
              value={optionsVolumeStart.find((item) => item.value === volumeUnitStart)}
            />
          </div>

          <span className="molarity-calculator__sign mb10 mr5 ml5">=</span>

          <div className="flex flex-ai-end">
            <FieldText
              name="concentrationEnd"
              placeholder="Your value"
              value={concentrationEnd}
              label={`Concentration <sub>(final)</sub>`}
              className="dilution-calculator__input mr10"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConcentrationEnd(e.target.value)}
            />
            <FieldSelect
              name="selectVolume"
              placeholder="Select"
              styles={styleSelectCalculator}
              options={optionsConcentrationEnd}
              onChange={(opt: TypeSelect) => setConcentrationUnitEnd(+(opt.value || 0))}
              value={optionsConcentrationEnd.find((item) => item.value === concentrationUnitEnd)}
            />
          </div>

          <span className="molarity-calculator__sign mb10 mr5 ml5">×</span>

          <div className="flex flex-ai-end">
            <FieldText
              name="volumeEnd"
              value={volumeEnd}
              placeholder="Your value"
              label={`Volume <sub>(final)</sub>`}
              className="dilution-calculator__input mr10"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setVolumeEbd(e.target.value)}
            />
            <FieldSelect
              name="volumeUnitEnd"
              placeholder="Select"
              options={optionsVolumeEnd}
              styles={styleSelectCalculator}
              onChange={(opt: TypeSelect) => setVolumeUnitEnd(+(opt.value || 0))}
              value={optionsVolumeEnd.find((item) => item.value === volumeUnitEnd)}
            />
          </div>
        </div>

        {errorMessage && <div className="molarity-calculator__error-message">{errorMessage}</div>}
      </div>
    </div>
  );
};

export default DilutionCalculator;